import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {BucketBrowserPageService} from '@services/left-menu/page/bucket-browser-page/bucket-browser-page.service';
import {PagesHelperService} from '@services/pages/pages-helper.service';
import {ActivatedRoute} from '@angular/router';
import {BucketBrowserPage} from '@models/sidebar/response/bucket-browser-page';
import {TranslateService} from '@ngx-translate/core';
import {DashboardLayouts} from '@models/dashboard/dashboard-layouts';
import {BucketBrowserFile} from '@models/pages/bucket-browser/bucket-browser-file';
import {BucketBrowserFolder} from '@models/pages/bucket-browser/bucket-browser-folder';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';

@Component({
    selector: 'scitis-bucket-browser',
    templateUrl: './bucket-browser-page.component.html',
    styleUrls: ['./bucket-browser-page.component.scss'],
    standalone: false
})
export class BucketBrowserPageComponent implements OnInit, OnChanges {

  @Input()
  public machineId: string = '';

  @Input()
  public pageId: string = '';

  @Input()
  public organizationId: string | null = null;

  @Input()
  public layout: DashboardLayouts = DashboardLayouts.TILE;

  @Input()
  public selectedItem: BucketBrowserFile | undefined = undefined;

  @Input()
  public searchResultItems: BucketBrowserFile[] = [];

  @Input()
  public closeSearchTimestamp: number = 0;

  @Output()
  public bucketBrowserPageDetailsUpdated: EventEmitter<BucketBrowserPage> = new EventEmitter<BucketBrowserPage>();

  @Output()
  public searchItemsUpdated: EventEmitter<BucketBrowserFile[]> = new EventEmitter<BucketBrowserFile[]>();

  @Output()
  public hideCloseSearchButtonTimestamp: EventEmitter<number> = new EventEmitter<number>();

  public bucketBrowserPageDetails: BucketBrowserPage | undefined = undefined;
  public files: BucketBrowserFile[] = [];
  public folders: BucketBrowserFolder[] = [];
  public searchItems: BucketBrowserFile[] = [];
  public currentLevel: string = '';
  public loading = true;
  public searchMode = false;
  public fallBackMessage: string = '';
  public showPage: boolean = true;

  private bucketBrowserLayouts = DashboardLayouts;
  private filesMessage: string = '';
  private fileMessage: string = '';
  private allItems: string[] = [];
  private showLevelUpButton = false;

  constructor(
      private activatedRoute: ActivatedRoute,
      private bucketBrowserPageService: BucketBrowserPageService,
      private pagesHelperService: PagesHelperService,
      private translate: TranslateService,
      private loadingIndicatorService: LoadingIndicatorService) {
    this.setTranslations();
  }

  get displayBackButton(): boolean {
    return this.currentLevel != '' && !this.loading && !this.searchMode;
  }

  get displaySearchDropdown(): boolean {
    return !this.loading && this.searchItems && this.searchItems.length > 0;
  }

  get tileMode(): boolean {
    return this.layout === this.bucketBrowserLayouts.TILE;
  }

  get listMode(): boolean {
    return this.layout === this.bucketBrowserLayouts.LIST;
  }

  private static containsObjectUpdateCount(obj, list, isFile): boolean {
    let listItem;
    for (listItem in list) {
      if (list.hasOwnProperty(listItem) && list[listItem].path === obj.path) {
        if (isFile) {
          list[listItem].count = list[listItem].count + 1;
        }
        return true;
      }
    }

    return false;
  }

  ngOnInit(): void {
    this.loadingIndicatorService.show();
    this.setBucketBrowserConfiguration();
    this.setBucketBrowserFiles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedItem) {
      if (this.selectedItem) {
        this.select(this.selectedItem);
      }
    }
    if (changes.searchResultItems && changes.searchResultItems.previousValue) {
      this.displaySearchResult();
    }

    if (changes.closeSearchTimestamp) {
      this.closeSearch();
    }
  }

  displayBadge(count: number): boolean {
    return count > 0;
  }

  openFolder(openedFolder: BucketBrowserFolder) {
    this.hideCloseSearchButtonTimestamp.emit(new Date().getTime());
    this.showLevelUpButton = true;
    this.folders = [];
    this.files = [];
    if (this.searchMode) {
      this.currentLevel = openedFolder.path;
    } else if (this.currentLevel === '') {
      this.currentLevel = openedFolder.name;
    } else {
      this.currentLevel = this.currentLevel + '/' + openedFolder.name;
    }

    this.searchMode = false;
    this.openFolderOfCurrentLevel();
  }

  levelUp() {
    this.currentLevel = this.currentLevel.substring(0, this.currentLevel.lastIndexOf('/'));
    this.openFolderOfCurrentLevel();
  }

  select(selectedItem: BucketBrowserFile): void {
    if (!this.searchMode) {
      this.openFolderOnSearchSelect(selectedItem);
    } else {
      this.openFileInNewWindow(selectedItem.path);
    }
  }

  getFilePath(filePath: string): string | null {
    if (this.organizationId) {
      return this.bucketBrowserPageService.getFileUrl(this.machineId, this.organizationId, filePath);
    }
    return null;
  }

  displaySearchResult() {
    this.searchMode = true;
    this.files = [];
    this.folders = [];

    this.searchResultItems.forEach(item => {
      if (item.isFolder) {
        const count = this.countFilesWithPath(item.path);
        const folder = new BucketBrowserFolder(item.name, item.path, count);
        this.folders.push(folder);

      } else {
        this.files.push(item);
      }
    });
  }

  closeSearch(): void {
    this.openFolderOfCurrentLevel();
    this.searchMode = false;
  }

  getFilesNumber(count: number): string {
    if (count === 1) {
      return `${count} ${this.fileMessage}`;
    } else {
      return `${count} ${this.filesMessage}`;
    }
  }

  private countFilesWithPath(path) {
    let count = -1;   //starting at -1 to not count the own item in
    this.allItems.forEach(item => {
      if (item.startsWith(path)) {
        count++;
      }
    });
    return count;
  }

  private setTranslations(): void {
    this.filesMessage = this.translate.instant('files');
    this.fileMessage = this.translate.instant('file');
    this.fallBackMessage = this.translate.instant('Bucket is empty!');
  }

  private setBucketBrowserConfiguration(): void {
    this.bucketBrowserPageService.getBucketBrowserPage(this.machineId, this.pageId)
    .subscribe((bucketBrowserPage: BucketBrowserPage) => {
      this.bucketBrowserPageDetails = bucketBrowserPage;
      this.bucketBrowserPageDetailsUpdated.emit(this.bucketBrowserPageDetails);
    }, error => {
      console.error('BucketBrowser. Error getting bucketBrowser configuration.', JSON.stringify(error));
    });
  }

  private setBucketBrowserFiles(): void {
    this.bucketBrowserPageService.getBucketBrowserPageDetails(this.machineId, this.pageId)
    .subscribe((success: string[]) => {
      this.bucketBrowserPageService.sortItems(success);
      this.allItems = success;
      this.folders = [];
      this.files = [];
      this.createSearchItems();
      this.currentLevel = '';
      this.loading = false;
      this.loadingIndicatorService.hide();
      this.openFolderOfCurrentLevel();
      if (!this.allItems || this.allItems.length === 0) {
        this.showPage = false;
      }
    }, error => {
      console.error('BucketBrowser. Error getting bucketBrowser content details.', JSON.stringify(error));
      this.loading = false;
      this.loadingIndicatorService.hide();
    });
  }

  private createSearchItems() {
    let id = 0;
    const pathMap: string[] = [];

    for (const item of this.allItems) {
      if (item.includes('/')) {
        const splitItems = item.split('/');

        for (let j = 0; j < splitItems.length; j++) {
          let isFolder = false;
          const pathArray = splitItems.slice(0, j + 1);
          const path: string = pathArray.join('/');

          if (pathMap.includes(path)) {
            continue;
          }

          if (j < splitItems.length - 1) {
            isFolder = true;
            pathMap.push(path);
          }

          const name = splitItems[j];
          id = this.addBucketBrowserFile(name, isFolder, path, id);
        }
      } else {
        id = this.addBucketBrowserFile(item, false, item, id);
      }
    }
  }

  private addBucketBrowserFile(name: string, isFolder: boolean, path: string, id: number): number {
    if (name !== '') {
      const file = new BucketBrowserFile(name, isFolder, path, id);
      this.searchItems.push(file);
      this.searchItemsUpdated.emit(this.searchItems);
      id++;
    }
    return id;
  }

  private openFolderOfCurrentLevel() {
    this.folders = [];
    this.files = [];
    this.allItems.forEach(item => {
      if (item.substring(0, this.currentLevel.length) === this.currentLevel) {
        item = this.getItem(item);
        const items = item.split('/');
        if (items.length > 1) {
          this.setFolders(items, item);
        } else {
          this.setFiles(item);
        }
      }
    });
  }

  private getItem(item: string): string {
    item = item.substring(this.currentLevel.length);
    if (item.startsWith('/')) {
      item = item.substring(1);
    }
    return item;
  }

  private setFolders(items: string[], item: string): void {
    const isFile = !item.endsWith('/');
    const folder = new BucketBrowserFolder(items[0], this.currentLevel + '/' + items[0], isFile ? 1 : 0);
    if (!BucketBrowserPageComponent.containsObjectUpdateCount(folder, this.folders, isFile)) {
      this.folders.push(folder);
    }
  }

  private setFiles(item: string): void {
    if (item && item !== '') {
      const file = new BucketBrowserFile(item, false, this.currentLevel + '/' + item, 0);
      if (!BucketBrowserPageComponent.containsObjectUpdateCount(file, this.files, true)) {
        this.files.push(file);
      }
    }
  }

  private openFolderOnSearchSelect(selectedItem: BucketBrowserFile): void {
    this.showLevelUpButton = true;
    this.folders = [];
    this.files = [];

    if (selectedItem.isFolder) {
      this.currentLevel = selectedItem.path;
      this.openFolderOfCurrentLevel();
    } else {
      const lastIndexSlash = selectedItem.path.lastIndexOf('/');
      this.currentLevel = selectedItem.path.substring(0, lastIndexSlash);
      const file = new BucketBrowserFolder(selectedItem.name, selectedItem.path, 1);

      this.openFolderOfCurrentLevel();
      this.openFileInNewWindow(file.path);
    }
  }

  private openFileInNewWindow(filePath: string): void {
    const fileFullPath = this.getFilePath(filePath);
    if (fileFullPath) {
      window.open(fileFullPath, '_blank');
    }
  }

}
